import {
  VaButton,
  VaButtonPair,
  VaModal,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { format, fromUnixTime, isBefore } from 'date-fns';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

/**
 * @param {string} date
 * @param {() => void} onStartNew
 */
const expiredContent = (date, onStartNew) => (
  <>
    <h3>Your saved question has expired</h3>
    <p>
      You started asking us a question but didn’t send it, so your saved
      question expired on <strong>{date}</strong>.
    </p>
    <p>To ask your question, you’ll need to start a new one.</p>
    <VaButton text="Start a new question" onClick={onStartNew} />
  </>
);

/**
 * @param {string} date
 * @param {() => void} onContinue
 * @param {() => void} openModal
 */
const inProgressContent = (date, onContinue, openModal) => (
  <>
    <h3>You have a question in progress</h3>
    <p>
      You started asking us a question but didn’t send it. You can continue
      asking your question now, or come back later to finish it. Or you can ask
      us a new question.
    </p>
    <p>
      Your saved question will expire on <strong>{date}</strong>.
    </p>
    <div>
      <VaButtonPair
        leftButtonText="Continue your question"
        rightButtonText="Start a new question"
        onPrimaryClick={onContinue}
        onSecondaryClick={openModal}
      />
    </div>
  </>
);

/**
 * @typedef {Object} AlertProps
 * @property {number} expiresAt - Unix timestamp in seconds representing when the saved form expires
 * @property {() => void} onContinue - Callback fired when the user clicks "Continue"
 * @property {() => void} onStartNew - Callback fired when the user clicks "Start a new question"
 */

/**
 * @param {AlertProps} props
 */
export default function QuestionInProgressAlert({
  expiresAt,
  onContinue,
  onStartNew,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const expirationDate = fromUnixTime(expiresAt);
  const isExpired = isBefore(expirationDate, new Date());
  const formattedDate = format(expirationDate, 'MMMM d, yyyy');

  const closeModal = () => setModalOpen(false);
  const openModal = () => setModalOpen(true);

  return (
    <va-alert status={isExpired ? 'warning' : 'info'}>
      {isExpired
        ? expiredContent(formattedDate, onStartNew)
        : inProgressContent(formattedDate, onContinue, openModal)}
      {modalOpen && (
        <VaModal
          id="start-new-question-modal"
          status="warning"
          clickToClose
          modalTitle="Starting a new question will delete your in-progress question"
          primaryButtonText="Start a new question"
          secondaryButtonText="Cancel"
          onCloseEvent={closeModal}
          onPrimaryButtonClick={() => {
            closeModal();
            onStartNew();
          }}
          onSecondaryButtonClick={closeModal}
          visible={modalOpen}
          uswds
        >
          <p>Are you sure you want to start a new question?</p>
        </VaModal>
      )}
    </va-alert>
  );
}

QuestionInProgressAlert.propTypes = {
  expiresAt: PropTypes.number.isRequired,
  onContinue: PropTypes.func.isRequired,
  onStartNew: PropTypes.func.isRequired,
};
